'use client';

import { type ElementType } from 'react';

import { type VariantProps } from 'tailwind-variants';

import {
  type PolymorphicComponent,
  type PolymorphicComponentProps,
} from '../../types/component';
import { tv } from '../../utils/tv';

const containerClasses = { root: 'zds-container-root' } as const;

type Variant = VariantProps<typeof containerStyles>;

const containerStyles = tv({
  base: `
    ${containerClasses.root}
    mx-auto w-full
  `,
  variants: {
    maxWidth: {
      xs: 'max-w-screen-xs',
      sm: 'max-w-screen-sm',
      md: 'max-w-screen-md',
      lg: 'max-w-screen-lg',
      xl: 'max-w-screen-xl',
      false: '',
    },
    disableGutters: {
      true: '',
      false: `
        px-4
        sm:px-6
      `,
    },
  },
  defaultVariants: {
    maxWidth: 'lg',
    disableGutters: false,
  },
});

interface ContainerBaseProps {
  /**
   * Removes horizontal padding from the `Container`.
   * @default false
   */
  disableGutters?: Variant['disableGutters'];

  /**
   * Maximum width of the `Container` based on breakpoint widths.
   * @default 'lg'
   */
  maxWidth?: Variant['maxWidth'];
}

type ContainerProps<RootComponent extends ElementType = 'div'> =
  PolymorphicComponentProps<ContainerBaseProps, RootComponent>;

type ContainerComponent = PolymorphicComponent<ContainerBaseProps, 'div'>;

const Container: ContainerComponent = (props) => {
  const {
    component: Root = 'div',
    maxWidth = 'lg',
    disableGutters = false,
    className,
    ref,
    ...rest
  } = props;

  const classes = containerStyles({
    disableGutters,
    maxWidth,
    className,
  });

  return <Root ref={ref} className={classes} {...rest} />;
};

Container.displayName = 'Container';

export {
  Container,
  type ContainerProps,
  type ContainerBaseProps,
  containerClasses,
};
